import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, MapPin, Sparkles } from "lucide-react";
import { CategoryIcon, CATEGORY_COLORS, SportIcon } from "@/components/CategoryIcon";
import { getOpenStatus } from "@/lib/hours";
import { SPORTS_TAGS, FITNESS_TAGS } from "@/lib/constants";
const ACTIVITY_TAGS = [...SPORTS_TAGS, ...FITNESS_TAGS];

export function PlaceCard({ place, index = 0, featured = false, fill = false }) {
  const color = CATEGORY_COLORS[place.category] || "#7A6A55";
  const rating = place.google_rating || place.average_community_rating;
  const status = getOpenStatus(place.hours, place.special_hours);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.4) }}
      className={`${featured ? "md:col-span-2 md:row-span-2" : ""} ${fill ? "h-full" : ""}`}
    >
      <Link
        to={`/place/${place.slug}`}
        data-testid={`place-card-${place.slug}`}
        className={`card-hover group overflow-hidden rounded-2xl border border-border bg-card shadow-sm ${fill ? "flex h-full flex-col" : "block"}`}
      >
        <div className={`relative overflow-hidden ${fill ? "min-h-0 flex-1" : featured ? "aspect-[16/10]" : "aspect-[4/3]"}`}>
          <img
            src={place.cover_image}
            alt={place.name}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <div className="absolute left-3 top-3 flex max-w-[85%] flex-wrap gap-2">
            <span
              className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-sm"
              style={{ backgroundColor: `${color}dd` }}
            >
              <CategoryIcon category={place.category} className="h-3 w-3" />
              {place.category}
            </span>
            {place.is_sponsored && (
              <span className="flex items-center gap-1 rounded-full bg-accent/90 px-2.5 py-1 text-[11px] font-semibold text-accent-foreground">
                <Sparkles className="h-3 w-3" /> Sponsored
              </span>
            )}
            {(place.tags || []).filter((t) => ACTIVITY_TAGS.includes(t)).slice(0, 3).map((t) => (
              <span key={t} title={t} data-testid={`place-sport-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center gap-1 rounded-full bg-white/90 px-2 py-1 text-[11px] font-semibold text-foreground backdrop-blur-sm">
                <SportIcon sport={t} className="h-3 w-3" />
              </span>
            ))}
          </div>
          {place.is_featured && !place.is_sponsored && (
            <span className="absolute right-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-bold text-primary">
              Featured
            </span>
          )}
          {status.status !== "unknown" && (
            <span
              data-testid={`place-status-badge-${place.slug}`}
              className={`absolute bottom-3 right-3 flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold backdrop-blur-sm ${status.closingSoon ? "bg-amber-500/95 text-white" : status.status === "open" ? "bg-green-500/90 text-white" : "bg-black/60 text-white"}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${status.status === "open" ? "bg-white" : "bg-red-400"}`} />
              {status.label}
            </span>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-4 pr-24">
            <h3 className={`font-serif font-bold text-white text-shadow-soft ${featured ? "text-3xl" : "text-xl"}`}>
              {place.name}
            </h3>
            {place.short_tagline && (
              <p className="mt-1 line-clamp-1 text-sm text-white/85 text-shadow-soft">{place.short_tagline}</p>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between px-4 py-3">
          <span className="flex items-center gap-1 text-sm text-muted-foreground">
            <MapPin size={13} /> {place.city}
          </span>
          <span className="flex items-center gap-2 text-sm">
            {rating > 0 && (
              <span className="flex items-center gap-1 font-semibold text-foreground">
                <Star size={13} className="fill-accent text-accent" /> {rating.toFixed(1)}
              </span>
            )}
            {place.price_range && <span className="text-secondary">{place.price_range}</span>}
          </span>
        </div>
        {status.status !== "unknown" && (
          <div
            data-testid={`place-status-${place.slug}`}
            className="flex items-center gap-1.5 border-t border-border px-4 py-2 text-xs"
          >
            <span className={`h-2 w-2 rounded-full ${status.closingSoon ? "bg-amber-500" : status.status === "open" ? "bg-green-500" : "bg-red-400"}`} />
            <span className={`font-semibold ${status.closingSoon ? "text-amber-600" : status.status === "open" ? "text-green-600" : "text-red-500"}`}>
              {status.label}
            </span>
            {status.today && <span className="text-muted-foreground">· {status.today}</span>}
          </div>
        )}
      </Link>
    </motion.div>
  );
}
